import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { Tabs, Tab, Spinner } from "@heroui/react";
import SearchFilter from "../../../utils/SearchFilter";
import ReportFilters, { getRangeDates, getTodayDate } from "./ReportFilters";
import ReportKpis from "./ReportKpis";
import ReportCharts from "./ReportCharts";
import { exportReportPdf, captureReportChartImages } from "./exportReportPdf";
import {
  MATKA_REPORT_ENDPOINTS,
  createMatkaReportDatasource,
  fetchMatkaReportOverview,
  fetchMatkaReportExport,
  slNoColumn,
  formatDrawDate,
} from "../../../utils/reportServerSide";

ModuleRegistry.registerModules([AllCommunityModule]);

const PAGE_SIZE = 20;

const moneyFormatter = (params) =>
  params.value != null ? Number(params.value).toFixed(2) : "0.00";

const profitLossClass = (params) =>
  params.value >= 0 ? "text-green-600" : "text-red-600";

const profitLossValueGetter = (params) => {
  const stake = Number(params.data?.total_stake) || 0;
  const payout = Number(params.data?.total_payout) || 0;
  return stake - payout;
};

const defaultInitialFilters = () => {
  const date = getTodayDate();
  const range = getRangeDates(date);
  return {
    date,
    start_date: range.start,
    end_date: range.end,
    market_id: "all",
    match_id: "all",
    operator_id: "all",
    status: "all",
    group_level: "summary",
    user_id: "all",
    user_ids: [],
  };
};

const marketColumns = [
  slNoColumn,
  { headerName: "Market", field: "market_name", colId: "market_name", minWidth: 160 },
  { headerName: "Matches", field: "match_count", colId: "match_count", maxWidth: 110 },
  { headerName: "Bets", field: "bet_count", colId: "bet_count", maxWidth: 100 },
  { headerName: "Users", field: "unique_users", colId: "unique_users", maxWidth: 100 },
  {
    headerName: "Total Stake",
    field: "total_stake",
    colId: "total_stake",
    valueFormatter: moneyFormatter,
  },
  {
    headerName: "Total Payout",
    field: "total_payout",
    colId: "total_payout",
    valueFormatter: moneyFormatter,
  },
  {
    headerName: "Profit / Loss",
    colId: "profit_loss",
    valueGetter: profitLossValueGetter,
    valueFormatter: moneyFormatter,
    cellClass: profitLossClass,
    sortable: false,
  },
];

const matchColumns = [
  slNoColumn,
  { headerName: "Market", field: "market_name", colId: "market_name", minWidth: 140 },
  { headerName: "Match", field: "match_name", colId: "match_name", minWidth: 140 },
  {
    headerName: "Draw Date",
    field: "draw_date",
    colId: "draw_date",
    maxWidth: 140,
    valueFormatter: (params) => formatDrawDate(params.value),
  },
  { headerName: "Bets", field: "bet_count", colId: "bet_count", maxWidth: 100 },
  { headerName: "Users", field: "unique_users", colId: "unique_users", maxWidth: 100 },
  {
    headerName: "Total Stake",
    field: "total_stake",
    colId: "total_stake",
    valueFormatter: moneyFormatter,
  },
  {
    headerName: "Total Payout",
    field: "total_payout",
    colId: "total_payout",
    valueFormatter: moneyFormatter,
  },
  {
    headerName: "Profit / Loss",
    colId: "profit_loss",
    valueGetter: profitLossValueGetter,
    valueFormatter: moneyFormatter,
    cellClass: profitLossClass,
    sortable: false,
  },
];

const userColumns = [
  slNoColumn,
  { headerName: "User ID", field: "user_id", colId: "user_id", maxWidth: 120 },
  { headerName: "Username", field: "username", colId: "username", minWidth: 140 },
  {
    headerName: "Market",
    field: "market_name",
    colId: "market_name",
    minWidth: 130,
    valueFormatter: (params) => params.value || "—",
  },
  {
    headerName: "Match",
    field: "match_name",
    colId: "match_name",
    minWidth: 130,
    valueFormatter: (params) => params.value || "—",
  },
  {
    headerName: "Draw Date",
    field: "draw_date",
    colId: "draw_date",
    maxWidth: 130,
    valueFormatter: (params) =>
      params.value ? formatDrawDate(params.value) : "—",
  },
  { headerName: "Bets", field: "bet_count", colId: "bet_count", maxWidth: 90 },
  {
    headerName: "Total Stake",
    field: "total_stake",
    colId: "total_stake",
    valueFormatter: moneyFormatter,
  },
  {
    headerName: "Total Payout",
    field: "total_payout",
    colId: "total_payout",
    valueFormatter: moneyFormatter,
  },
  {
    headerName: "Profit / Loss",
    colId: "profit_loss",
    valueGetter: profitLossValueGetter,
    valueFormatter: moneyFormatter,
    cellClass: profitLossClass,
    sortable: false,
  },
];

const TAB_CONFIG = {
  markets: {
    endpoint: MATKA_REPORT_ENDPOINTS.byMarket,
    columns: marketColumns,
    filterProps: { showMatch: false, showUser: false, showGroupLevel: false },
  },
  matches: {
    endpoint: MATKA_REPORT_ENDPOINTS.byMatch,
    columns: matchColumns,
    filterProps: { showMatch: true, showUser: false, showGroupLevel: false },
  },
  users: {
    endpoint: MATKA_REPORT_ENDPOINTS.byUser,
    columns: userColumns,
    filterProps: { showMatch: true, showUser: true, showGroupLevel: true },
  },
};

const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState("markets");
  const gridRef = useRef(null);
  const searchRef = useRef("");
  const filtersRef = useRef(defaultInitialFilters());
  const [gridReady, setGridReady] = useState(false);
  const [quickFilterText, setQuickFilterText] = useState("");
  const [filtersKey, setFiltersKey] = useState(0);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const tabConfig = TAB_CONFIG[activeTab];

  const handleApplyFilters = useCallback((filters) => {
    filtersRef.current = filters;
    setFiltersKey((k) => k + 1);
  }, []);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const data = await fetchMatkaReportOverview({
        ...filtersRef.current,
        tab: activeTab,
      });
      setOverview(data);
    } catch (error) {
      console.error("Failed to load report overview:", error);
      setOverview(null);
    } finally {
      setOverviewLoading(false);
    }
  }, [activeTab, filtersKey]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const datasource = useMemo(
    () =>
      createMatkaReportDatasource({
        endpoint: tabConfig.endpoint,
        getBasePayload: () => filtersRef.current,
        getSearch: () => searchRef.current,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTab, filtersKey]
  );

  const onGridReady = useCallback(
    (params) => {
      gridRef.current = params.api;
      params.api.setGridOption("datasource", datasource);
      setGridReady(true);
    },
    [datasource]
  );

  useEffect(() => {
    searchRef.current = quickFilterText;
    if (!gridReady || !gridRef.current) return;

    const timer = setTimeout(() => {
      gridRef.current.paginationGoToFirstPage();
      gridRef.current.refreshInfiniteCache();
    }, 300);

    return () => clearTimeout(timer);
  }, [quickFilterText, gridReady]);

  useEffect(() => {
    if (!gridReady || !gridRef.current) return;
    gridRef.current.setGridOption("datasource", datasource);
    gridRef.current.paginationGoToFirstPage();
    gridRef.current.purgeInfiniteCache();
  }, [filtersKey, gridReady, datasource, activeTab]);

  useEffect(() => {
    setQuickFilterText("");
    searchRef.current = "";
    setGridReady(false);
  }, [activeTab]);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const data = await fetchMatkaReportExport({
        ...filtersRef.current,
        tab: activeTab,
      });

      const chartsRoot = document.getElementById("report-charts-export");
      const chartImages = await captureReportChartImages(chartsRoot);

      await exportReportPdf({
        tab: activeTab,
        filters: data.filters || filtersRef.current,
        kpis: data.kpis,
        rows: data.rows || [],
        charts: data.charts,
        sessionBreakdown: data.sessionBreakdown,
        chartImages,
      });
    } catch (error) {
      console.error("PDF export failed:", error);
      alert(error.message || "Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      flex: 1,
      minWidth: 100,
    }),
    []
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-semibold text-gray-900">Reports</h2>
        <button
          type="button"
          onClick={handleExportPdf}
          disabled={exporting}
          className="bg-indigo-600 text-white px-4 py-2 rounded text-sm hover:bg-indigo-700 disabled:opacity-60"
        >
          {exporting ? "Exporting…" : "Export PDF"}
        </button>
      </div>

      <Tabs
        selectedKey={activeTab}
        onSelectionChange={(key) => setActiveTab(String(key))}
        aria-label="Report tabs"
        color="primary"
        variant="underlined"
      >
        <Tab key="markets" title="Markets" />
        <Tab key="matches" title="Matches" />
        <Tab key="users" title="Users" />
      </Tabs>

      <ReportFilters
        key={activeTab}
        onApply={handleApplyFilters}
        {...tabConfig.filterProps}
      />

      {overviewLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <>
          <ReportKpis tab={activeTab} kpis={overview?.kpis} />
          <div id="report-charts-export">
            <ReportCharts tab={activeTab} overview={overview} />
          </div>
        </>
      )}

      <SearchFilter
        value={quickFilterText}
        onChange={(e) => setQuickFilterText(e.target.value)}
      />

      <div className="ag-theme-alpine" style={{ height: 560, width: "100%" }}>
        <AgGridReact
          key={activeTab}
          columnDefs={tabConfig.columns}
          defaultColDef={defaultColDef}
          rowModelType="infinite"
          cacheBlockSize={PAGE_SIZE}
          maxBlocksInCache={5}
          pagination={true}
          paginationPageSize={PAGE_SIZE}
          onGridReady={onGridReady}
          onSortChanged={(params) => params.api.refreshInfiniteCache()}
          enableCellTextSelection={true}
          overlayNoRowsTemplate="<span style='padding: 10px; display: block;'>No data found for selected filters</span>"
        />
      </div>
    </div>
  );
};

export default ReportsPage;
